<template>
  <div class="admin-lead-form-submissions p-4 sm:p-6 lg:p-8">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">Submissions - {{ formTitle }}</h1>
        <p class="text-gray-600 mt-1">View all submissions for this lead form</p>
      </div>
      <div class="flex items-center gap-2">
        <Link
          :href="route('admin.lead-forms.show', formId)"
          class="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Back to Form
        </Link>
        <a
          :href="route('lead-forms.export', formId)"
          class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
        >
          <icon name="download" class="w-5 h-5" />
          Export CSV
        </a>
      </div>
    </div>

    <!-- Submissions Table -->
    <div class="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              <th
                v-for="field in fieldLabels"
                :key="field"
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                {{ field }}
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="lead in leads" :key="lead.id" class="hover:bg-gray-50 transition-colors">
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ formatDate(lead.captured_at) }}
              </td>
              <td
                v-for="field in fieldLabels"
                :key="field"
                class="px-6 py-4 whitespace-nowrap text-sm text-gray-700"
              >
                {{ lead.data && lead.data[field] ? lead.data[field] : '-' }}
              </td>
            </tr>
            <tr v-if="leads.length === 0">
              <td :colspan="fieldLabels.length + 1" class="px-6 py-12 text-center">
                <div class="flex flex-col items-center gap-3">
                  <icon name="contact" class="w-16 h-16 text-gray-300" />
                  <p class="text-gray-500 text-lg font-medium">No submissions yet</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="pagination && pagination.total > pagination.per_page" class="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-between">
        <div class="text-sm text-gray-700">
          Showing {{ pagination.from }} to {{ pagination.to }} of {{ pagination.total }} results
        </div>
        <div class="flex gap-2">
          <button
            @click="loadPage(pagination.current_page - 1)"
            :disabled="pagination.current_page === 1"
            class="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>
          <button
            @click="loadPage(pagination.current_page + 1)"
            :disabled="pagination.current_page === pagination.last_page"
            class="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Link, router } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon.vue'
import Layout from '@/Shared/Layout'

export default {
  layout: Layout,
  components: {
    Link,
    Icon,
  },
  props: {
    formId: {
      type: Number,
      required: true,
    },
    formTitle: {
      type: String,
      required: true,
    },
    leads: {
      type: Array,
      default: () => [],
    },
    pagination: {
      type: Object,
      default: () => ({}),
    },
    title: {
      type: String,
      default: 'Form Submissions',
    },
  },
  computed: {
    fieldLabels() {
      if (!this.leads || this.leads.length === 0) return []
      const allFields = new Set()
      this.leads.forEach(lead => {
        if (lead.data && typeof lead.data === 'object') {
          Object.keys(lead.data).forEach(key => allFields.add(key))
        }
      })
      return Array.from(allFields)
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return 'N/A'
      const d = new Date(date)
      return d.toLocaleDateString() + ' ' + d.toLocaleTimeString()
    },
    loadPage(page) {
      router.get(route('admin.lead-forms.submissions', this.formId), { page }, {
        preserveState: true,
        preserveScroll: true,
      })
    },
  },
}
</script>

<style scoped>
.admin-lead-form-submissions {
  min-height: 100vh;
  background-color: #f9fafb;
}
</style>



